/**
 * RefereeSection - Referee panel with user list and actions
 *
 * Similar layout to WorkerSection but for referees:
 * - Lists referee users (users referred by this referee)
 * - Hamburger menu for dashboard stats
 * - Send Token (with bonus display)
 * - Send Withdrawal
 *
 * Uses shared components:
 * - InfoCard/InfoCardList (composed) - user items
 * - BottomSheet (core) - detail container
 * - PopupMessage (core) - action confirmations
 */

import { useState, useCallback, useEffect } from 'react';
import { RefreshCw, Users, Menu, X, Wallet, Coins, ArrowDownToLine, ArrowUpFromLine } from 'lucide-react';
import {
  InfoCard,
  InfoCardList,
  BottomSheet,
  PopupMessage,
} from '@/components/shared';
import { StaffLayout } from '@/components/layout';
import { cn } from '@/lib/utils';
import type { RefereeBalanceLogEntry, RefereeConversion, RefereeWithdrawal } from '@/types';

// ============================================
// TYPES
// ============================================

export interface RefereeProfile {
  identifier: string;
  referralCode: string;
  name: string | null;
  commissionPercent: number;
  totalEarned: number;
  totalLost: number;
  totalWithdrawn: number;
  totalConverted: number;
  net: number;
  balance: number;
  createdAt: string;
}

export interface RefereeSectionProps {
  /** Referee profile data */
  profile: RefereeProfile | null;
  /** Commission history (balance log) */
  balanceLogs: RefereeBalanceLogEntry[];
  /** Token conversions history */
  conversions: RefereeConversion[];
  /** Referee withdrawals history */
  withdrawals: RefereeWithdrawal[];
  /** Loading state */
  isLoading?: boolean;
  /** Profile loading */
  isLoadingProfile?: boolean;
  /** Logs loading */
  isLoadingLogs?: boolean;
  /** Conversions loading */
  isLoadingConversions?: boolean;
  /** Withdrawals loading */
  isLoadingWithdrawals?: boolean;
  /** Token conversion in progress */
  isConverting?: boolean;
  /** Withdrawal in progress */
  isCreatingWithdrawal?: boolean;
  /** Bonus percentage for token conversion */
  conversionBonusPercent?: number;
  /** Refresh profile */
  onRefreshProfile?: () => void;
  /** Fetch balance logs */
  onFetchLogs?: () => void;
  /** Fetch more logs */
  onFetchMoreLogs?: () => void;
  /** Has more logs */
  hasMoreLogs?: boolean;
  /** Fetch conversions */
  onFetchConversions?: () => void;
  /** Fetch withdrawals */
  onFetchWithdrawals?: () => void;
  /** Send tokens handler */
  onSendTokens?: (targetTelegramId: string, amount: number) => Promise<void>;
  /** Send withdrawal handler */
  onSendWithdrawal?: (amount: number, cardNumber: string) => Promise<void>;
  /** Error */
  error?: string | null;
}

type PopupType = 'sendTokens' | 'sendWithdrawal' | 'withdrawNudge' | null;

// Unified activity item for the "Activity" tab (conversions + withdrawals)
type ActivityType = 'withdrawal' | 'send_tokens';

interface ActivityItem {
  id: string;
  type: ActivityType;
  amount: number;
  status: string;
  createdAt: string;
  // Original data for detail view
  withdrawal?: RefereeWithdrawal;
  conversion?: RefereeConversion;
}

// ============================================
// STAT CARD COMPONENT
// ============================================

interface StatCardProps {
  label: string;
  value: string | number;
  icon: React.ReactNode;
  color?: 'green' | 'red' | 'blue' | 'amber';
}

function StatCard({ label, value, icon, color = 'blue' }: StatCardProps) {
  const colorClasses = {
    green: 'bg-emerald-50 text-emerald-600 border-emerald-200',
    red: 'bg-rose-50 text-rose-600 border-rose-200',
    blue: 'bg-sky-50 text-sky-600 border-sky-200',
    amber: 'bg-amber-50 text-amber-600 border-amber-200',
  };

  return (
    <div className={cn(
      'flex items-center gap-3 p-3 rounded-xl border',
      colorClasses[color],
    )}>
      <div className="flex-shrink-0">
        {icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-slate-500">{label}</p>
        <p className="text-lg font-bold truncate">{value}</p>
      </div>
    </div>
  );
}

// ============================================
// COMPONENT
// ============================================

export function RefereeSection({
  profile,
  balanceLogs,
  conversions,
  withdrawals,
  isLoading: _isLoading = false,
  isLoadingProfile = false,
  isLoadingLogs = false,
  isLoadingConversions = false,
  isLoadingWithdrawals = false,
  isConverting = false,
  isCreatingWithdrawal = false,
  conversionBonusPercent = 10, // Default 10% bonus
  onRefreshProfile,
  onFetchLogs,
  onFetchMoreLogs,
  hasMoreLogs = false,
  onFetchConversions,
  onFetchWithdrawals,
  onSendTokens,
  onSendWithdrawal,
  error,
}: RefereeSectionProps) {
  // Menu state (hamburger -> dashboard stats)
  const [menuOpen, setMenuOpen] = useState(false);

  // View state
  const [view, setView] = useState<'users' | 'conversions'>('users');

  // Detail sheet state (for commission log)
  const [detailOpen, setDetailOpen] = useState(false);
  const [selectedLog, setSelectedLog] = useState<RefereeBalanceLogEntry | null>(null);

  // Activity detail sheet state (for withdrawals/conversions)
  const [activityDetailOpen, setActivityDetailOpen] = useState(false);
  const [selectedActivity, setSelectedActivity] = useState<ActivityItem | null>(null);

  // Popup state
  const [popup, setPopup] = useState<PopupType>(null);
  const [actionLoading, setActionLoading] = useState(false);

  // Token conversion form state
  const [tokenAmount, setTokenAmount] = useState('');
  const [tokenTargetId, setTokenTargetId] = useState('');

  // Withdrawal form state
  const [withdrawalAmount, setWithdrawalAmount] = useState('');
  const [withdrawalCard, setWithdrawalCard] = useState('');

  // Fetch logs on mount
  useEffect(() => {
    if (balanceLogs.length === 0 && onFetchLogs && !isLoadingLogs) {
      onFetchLogs();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Open log detail
  const handleOpenDetail = useCallback((log: RefereeBalanceLogEntry) => {
    setSelectedLog(log);
    setDetailOpen(true);
  }, []);

  // Close detail
  const handleCloseDetail = useCallback(() => {
    setDetailOpen(false);
    setSelectedLog(null);
  }, []);

  // Handle send tokens
  const handleSendTokens = useCallback(async () => {
    if (!onSendTokens || !tokenTargetId || !tokenAmount) return;
    const amount = parseFloat(tokenAmount);
    if (isNaN(amount) || amount <= 0) return;

    setActionLoading(true);
    try {
      await onSendTokens(tokenTargetId, amount);
      setTokenAmount('');
      setTokenTargetId('');
      setPopup(null);
    } finally {
      setActionLoading(false);
    }
  }, [onSendTokens, tokenTargetId, tokenAmount]);

  // Handle send withdrawal
  const handleSendWithdrawal = useCallback(async () => {
    if (!onSendWithdrawal || !withdrawalCard || !withdrawalAmount) return;
    const amount = parseFloat(withdrawalAmount);
    if (isNaN(amount) || amount <= 0) return;

    setActionLoading(true);
    try {
      await onSendWithdrawal(amount, withdrawalCard);
      setWithdrawalAmount('');
      setWithdrawalCard('');
      setPopup(null);
    } finally {
      setActionLoading(false);
    }
  }, [onSendWithdrawal, withdrawalCard, withdrawalAmount]);

  // Calculate bonus for token conversion
  const tokenBonusAmount = tokenAmount
    ? (parseFloat(tokenAmount) * conversionBonusPercent / 100) || 0
    : 0;
  const tokenTotalWithBonus = tokenAmount
    ? (parseFloat(tokenAmount) || 0) + tokenBonusAmount
    : 0;

  // Minimum withdrawal amount for referees
  const MIN_REFEREE_WITHDRAWAL = 100;
  const withdrawalAmountNum = parseFloat(withdrawalAmount) || 0;
  const isWithdrawalAmountValid = withdrawalAmountNum >= MIN_REFEREE_WITHDRAWAL && withdrawalAmountNum <= (profile?.balance || 0);

  // Format currency
  const formatCurrency = (val: number) => `${val.toFixed(2)} AZN`;

  // Format user display name (username > firstName lastName > telegramId)
  const formatUserDisplay = (log: RefereeBalanceLogEntry): string => {
    if (log.username) return `@${log.username}`;
    const nameParts = [log.firstName, log.lastName].filter(Boolean);
    if (nameParts.length > 0) return nameParts.join(' ');
    return log.userTelegramId;
  };

  // Create unified activity list (withdrawals + conversions sorted by date)
  const activityList: ActivityItem[] = [
    // Map withdrawals
    ...withdrawals.map((w): ActivityItem => ({
      id: `w-${w.referenceNumber}`,
      type: 'withdrawal',
      amount: w.amount,
      status: w.status,
      createdAt: w.createdAt,
      withdrawal: w,
    })),
    // Map conversions
    ...conversions.map((c): ActivityItem => ({
      id: `c-${c.id}`,
      type: 'send_tokens',
      amount: c.totalCredited ?? c.amount,
      status: c.status,
      createdAt: c.createdAt,
      conversion: c,
    })),
  ].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

  // Open activity detail
  const handleOpenActivityDetail = useCallback((activity: ActivityItem) => {
    setSelectedActivity(activity);
    setActivityDetailOpen(true);
  }, []);

  // Close activity detail
  const handleCloseActivityDetail = useCallback(() => {
    setActivityDetailOpen(false);
    setSelectedActivity(null);
  }, []);

  return (
    <StaffLayout>
      <div className="flex flex-col h-full bg-slate-100">
        {/* Header */}
        <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-b border-slate-200">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold text-slate-800">Referee Panel</h1>
              <p className="text-sm text-slate-500">
                Balance: {profile ? formatCurrency(profile.balance) : '-'}
                {conversionBonusPercent > 0 && (
                  <span className="ml-2 px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-600 text-xs font-medium">
                    +{conversionBonusPercent}% bonus
                  </span>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onRefreshProfile}
                disabled={isLoadingProfile}
                className={cn(
                  'p-2.5 rounded-full',
                  'bg-white border border-slate-200',
                  'shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
                  'transition-all',
                  isLoadingProfile && 'opacity-50',
                )}
              >
                <RefreshCw className={cn('w-5 h-5 text-slate-600', isLoadingProfile && 'animate-spin')} />
              </button>
              {/* Hamburger Menu */}
              <button
                type="button"
                onClick={() => setMenuOpen(true)}
                className={cn(
                  'p-2.5 rounded-full',
                  'bg-white border border-slate-200',
                  'shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
                  'transition-all',
                )}
              >
                <Menu className="w-5 h-5 text-slate-600" />
              </button>
            </div>
          </div>
        </div>

        {/* Tab Switcher */}
        <div className="flex-shrink-0 px-4 py-2 bg-slate-50 border-b border-slate-200">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setView('users')}
              className={cn(
                'flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all',
                view === 'users'
                  ? 'bg-sky-500 text-white'
                  : 'bg-white text-slate-600 border border-slate-200',
              )}
            >
              Commission Log
            </button>
            <button
              type="button"
              onClick={() => {
                setView('conversions');
                // Fetch both conversions and withdrawals for unified activity list
                if (conversions.length === 0 && onFetchConversions) {
                  onFetchConversions();
                }
                if (withdrawals.length === 0 && onFetchWithdrawals) {
                  onFetchWithdrawals();
                }
              }}
              className={cn(
                'flex-1 py-2 px-4 rounded-full text-sm font-medium transition-all',
                view === 'conversions'
                  ? 'bg-sky-500 text-white'
                  : 'bg-white text-slate-600 border border-slate-200',
              )}
            >
              Activity
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-4 py-4">
          {error && (
            <div className="mb-4 p-3 rounded-xl bg-rose-50 text-rose-600 text-sm">
              {error}
            </div>
          )}

          {view === 'users' ? (
            // Commission log (balance log = users who made deposits/withdrawals)
            <>
              {isLoadingLogs && balanceLogs.length === 0 ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-14 rounded-full bg-white animate-pulse" />
                  ))}
                </div>
              ) : balanceLogs.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Users className="w-12 h-12 text-slate-300 mb-3" />
                  <p className="text-slate-400 text-sm">No commission history yet</p>
                </div>
              ) : (
                <>
                  <InfoCardList>
                    {balanceLogs.map((log) => (
                      <InfoCard
                        key={log.id}
                        label={`${log.isEarning ? '+' : '-'}${log.commissionAmount.toFixed(2)} AZN`}
                        type="user"
                        searchHighlights={[
                          log.eventType === 'user_deposit' ? 'Deposit' : 'Withdrawal',
                          formatUserDisplay(log),
                        ]}
                        onClick={() => handleOpenDetail(log)}
                      />
                    ))}
                  </InfoCardList>
                  {hasMoreLogs && (
                    <button
                      type="button"
                      onClick={onFetchMoreLogs}
                      disabled={isLoadingLogs}
                      className={cn(
                        'w-full mt-4 py-3 rounded-full',
                        'bg-white border border-slate-200',
                        'text-sm font-medium text-slate-600',
                        'shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
                        isLoadingLogs && 'opacity-50',
                      )}
                    >
                      {isLoadingLogs ? 'Loading...' : 'Load More'}
                    </button>
                  )}
                </>
              )}
            </>
          ) : (
            // Activity history (withdrawals + conversions)
            <>
              {(isLoadingConversions || isLoadingWithdrawals) && activityList.length === 0 ? (
                <div className="space-y-2">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-14 rounded-full bg-white animate-pulse" />
                  ))}
                </div>
              ) : activityList.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <Coins className="w-12 h-12 text-slate-300 mb-3" />
                  <p className="text-slate-400 text-sm">No activity yet</p>
                </div>
              ) : (
                <InfoCardList>
                  {activityList.map((activity) => (
                    <InfoCard
                      key={activity.id}
                      label={activity.type === 'withdrawal' ? 'Withdrawal' : 'Send Tokens'}
                      type={activity.type === 'withdrawal' ? 'withdrawal' : 'deposit'}
                      searchHighlights={[
                        activity.type === 'withdrawal' ? 'Withdrawal' : 'Send Tokens',
                        `${activity.amount.toFixed(2)} AZN`,
                      ]}
                      onClick={() => handleOpenActivityDetail(activity)}
                    />
                  ))}
                </InfoCardList>
              )}
            </>
          )}
        </div>

        {/* Action Buttons */}
        <div className="flex-shrink-0 px-4 py-3 bg-slate-50 border-t border-slate-200">
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setPopup('sendTokens')}
              disabled={!profile || profile.balance <= 0}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-3 rounded-full',
                'text-sm font-semibold',
                'transition-all',
                'shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
                profile && profile.balance > 0
                  ? 'bg-emerald-500 text-white hover:bg-emerald-600'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed',
              )}
            >
              <Coins className="w-4 h-4" />
              Send Tokens
            </button>
            <button
              type="button"
              onClick={() => setPopup(conversionBonusPercent > 0 ? 'withdrawNudge' : 'sendWithdrawal')}
              disabled={!profile || profile.balance <= 0}
              className={cn(
                'flex-1 flex items-center justify-center gap-2 py-3 rounded-full',
                'text-sm font-semibold',
                'transition-all',
                'shadow-[0_2px_8px_rgba(0,0,0,0.06)]',
                profile && profile.balance > 0
                  ? 'bg-sky-500 text-white hover:bg-sky-600'
                  : 'bg-slate-200 text-slate-400 cursor-not-allowed',
              )}
            >
              <ArrowUpFromLine className="w-4 h-4" />
              Withdraw
            </button>
          </div>
        </div>
      </div>

      {/* Dashboard Menu (Slide-in panel) */}
      <div
        className={cn(
          'fixed inset-0 z-50 transition-opacity duration-300',
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none',
        )}
      >
        {/* Backdrop */}
        <div
          className="absolute inset-0 bg-black/50"
          onClick={() => setMenuOpen(false)}
        />

        {/* Panel */}
        <div
          className={cn(
            'absolute top-0 right-0 bottom-0 w-80 bg-white',
            'shadow-xl transition-transform duration-300',
            menuOpen ? 'translate-x-0' : 'translate-x-full',
          )}
        >
          {/* Panel Header */}
          <div className="flex items-center justify-between px-4 py-4 border-b border-slate-200">
            <h2 className="text-lg font-bold text-slate-800">Dashboard</h2>
            <button
              type="button"
              onClick={() => setMenuOpen(false)}
              className="p-2 rounded-full hover:bg-slate-100 transition-colors"
            >
              <X className="w-5 h-5 text-slate-600" />
            </button>
          </div>

          {/* Panel Content */}
          <div className="p-4 space-y-3 overflow-y-auto max-h-[calc(100vh-120px)]">
            {isLoadingProfile && !profile ? (
              <div className="space-y-3">
                {[1, 2, 3, 4].map((i) => (
                  <div key={i} className="h-16 rounded-xl bg-slate-100 animate-pulse" />
                ))}
              </div>
            ) : profile ? (
              <>
                <StatCard
                  label="Current Balance"
                  value={formatCurrency(profile.balance)}
                  icon={<Wallet className="w-5 h-5" />}
                  color="blue"
                />
                <StatCard
                  label="Total Earned"
                  value={formatCurrency(profile.totalEarned)}
                  icon={<ArrowDownToLine className="w-5 h-5" />}
                  color="green"
                />
                <StatCard
                  label="Total Lost"
                  value={formatCurrency(profile.totalLost)}
                  icon={<ArrowUpFromLine className="w-5 h-5" />}
                  color="red"
                />
                <StatCard
                  label="Net Earnings"
                  value={formatCurrency(profile.net)}
                  icon={<Coins className="w-5 h-5" />}
                  color={profile.net >= 0 ? 'green' : 'red'}
                />
                <StatCard
                  label="Total Withdrawn"
                  value={formatCurrency(profile.totalWithdrawn)}
                  icon={<ArrowUpFromLine className="w-5 h-5" />}
                  color="amber"
                />
                <StatCard
                  label="Total Converted"
                  value={formatCurrency(profile.totalConverted)}
                  icon={<Coins className="w-5 h-5" />}
                  color="amber"
                />

                {/* Additional info */}
                <div className="mt-4 p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Referral Code</p>
                  <p className="text-lg font-bold text-slate-800">{profile.referralCode}</p>
                </div>
                <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
                  <p className="text-xs text-slate-500 mb-1">Commission Rate</p>
                  <p className="text-lg font-bold text-slate-800">{profile.commissionPercent}%</p>
                </div>
              </>
            ) : (
              <div className="text-center py-8 text-slate-400">
                Failed to load profile
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Log Detail Bottom Sheet */}
      <BottomSheet
        isOpen={detailOpen}
        onClose={handleCloseDetail}
        title="Commission Details"
      >
        {selectedLog && (
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Type</span>
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                selectedLog.isEarning ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600',
              )}>
                {selectedLog.eventType === 'user_deposit' ? 'User Deposit' : 'User Withdrawal'}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">User</span>
              <span className="text-sm font-medium text-slate-800">{formatUserDisplay(selectedLog)}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Telegram ID</span>
              <span className="text-sm font-medium text-slate-800 font-mono">{selectedLog.userTelegramId}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Transaction Amount</span>
              <span className="text-sm font-medium text-slate-800">{selectedLog.transactionAmount} AZN</span>
            </div>
            {selectedLog.usdtAmount && (
              <div className="flex items-center justify-between py-3 border-b border-slate-100">
                <span className="text-sm text-slate-500">USDT Amount</span>
                <span className="text-sm font-medium text-slate-800">{selectedLog.usdtAmount} USDT</span>
              </div>
            )}
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Commission Rate</span>
              <span className="text-sm font-medium text-slate-800">{selectedLog.commissionPercent}%</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Commission Amount</span>
              <span className={cn(
                'text-sm font-bold',
                selectedLog.isEarning ? 'text-emerald-600' : 'text-rose-600',
              )}>
                {selectedLog.isEarning ? '+' : '-'}{selectedLog.commissionAmount} AZN
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Reference</span>
              <span className="text-sm font-medium text-slate-800">{selectedLog.referenceNumber}</span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-slate-500">Date</span>
              <span className="text-sm font-medium text-slate-800">
                {new Date(selectedLog.createdAt).toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Activity Detail Bottom Sheet */}
      <BottomSheet
        isOpen={activityDetailOpen}
        onClose={handleCloseActivityDetail}
        title={selectedActivity?.type === 'withdrawal' ? 'Withdrawal Details' : 'Token Conversion Details'}
      >
        {selectedActivity && selectedActivity.type === 'withdrawal' && selectedActivity.withdrawal && (
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Type</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-sky-50 text-sky-600">
                Withdrawal
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Reference</span>
              <span className="text-sm font-medium text-slate-800">{selectedActivity.withdrawal.referenceNumber}</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Amount</span>
              <span className="text-sm font-bold text-slate-800">{selectedActivity.withdrawal.amount} AZN</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Card Number</span>
              <span className="text-sm font-medium text-slate-800 font-mono">
                {selectedActivity.withdrawal.cardNumber ? `****${selectedActivity.withdrawal.cardNumber.slice(-4)}` : '-'}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Status</span>
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                selectedActivity.withdrawal.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                selectedActivity.withdrawal.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                selectedActivity.withdrawal.status === 'declined' ? 'bg-rose-50 text-rose-600' :
                'bg-slate-50 text-slate-600',
              )}>
                {selectedActivity.withdrawal.status}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-slate-500">Date</span>
              <span className="text-sm font-medium text-slate-800">
                {new Date(selectedActivity.withdrawal.createdAt).toLocaleString()}
              </span>
            </div>
          </div>
        )}
        {selectedActivity && selectedActivity.type === 'send_tokens' && selectedActivity.conversion && (
          <div className="p-4 space-y-3">
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Type</span>
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-50 text-emerald-600">
                Send Tokens
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Amount</span>
              <span className="text-sm font-medium text-slate-800">{selectedActivity.conversion.amount} AZN</span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Bonus</span>
              <span className="text-sm font-medium text-emerald-600">
                +{selectedActivity.conversion.bonusAmount} AZN ({selectedActivity.conversion.bonusPercent}%)
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Total Credited</span>
              <span className="text-sm font-bold text-slate-800">
                {selectedActivity.conversion.totalCredited ?? selectedActivity.conversion.amount} tokens
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Target User</span>
              <span className="text-sm font-medium text-slate-800">
                {selectedActivity.conversion.targetUsername || selectedActivity.conversion.targetTelegramId || '-'}
              </span>
            </div>
            <div className="flex items-center justify-between py-3 border-b border-slate-100">
              <span className="text-sm text-slate-500">Status</span>
              <span className={cn(
                'px-2 py-0.5 rounded-full text-xs font-medium',
                selectedActivity.conversion.status === 'completed' ? 'bg-emerald-50 text-emerald-600' :
                selectedActivity.conversion.status === 'pending' ? 'bg-amber-50 text-amber-600' :
                selectedActivity.conversion.status === 'failed' ? 'bg-rose-50 text-rose-600' :
                'bg-slate-50 text-slate-600',
              )}>
                {selectedActivity.conversion.status}
              </span>
            </div>
            <div className="flex items-center justify-between py-3">
              <span className="text-sm text-slate-500">Date</span>
              <span className="text-sm font-medium text-slate-800">
                {new Date(selectedActivity.conversion.createdAt).toLocaleString()}
              </span>
            </div>
          </div>
        )}
      </BottomSheet>

      {/* Send Tokens Popup */}
      <PopupMessage
        isOpen={popup === 'sendTokens'}
        onClose={() => {
          setPopup(null);
          setTokenAmount('');
          setTokenTargetId('');
        }}
        title="Send Tokens"
        message={`Convert your balance to game tokens for any player. Current bonus: ${conversionBonusPercent}%`}
        declineLabel="Cancel"
        proceedLabel="Send"
        proceedVariant="success"
        isLoading={actionLoading || isConverting}
        onDecline={() => {
          setPopup(null);
          setTokenAmount('');
          setTokenTargetId('');
        }}
        onProceed={() => {
          handleSendTokens();
        }}
        customContent={
          <div className="space-y-3 mt-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Player Telegram ID</label>
              <input
                type="text"
                value={tokenTargetId}
                onChange={(e) => setTokenTargetId(e.target.value)}
                placeholder="Enter player ID"
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-50 border border-slate-200',
                  'text-sm text-slate-800',
                  'focus:outline-none focus:ring-2 focus:ring-sky-500',
                )}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Amount (AZN)</label>
              <input
                type="number"
                value={tokenAmount}
                onChange={(e) => setTokenAmount(e.target.value)}
                placeholder="Enter amount"
                min="1"
                max={profile?.balance || 0}
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-50 border border-slate-200',
                  'text-sm text-slate-800',
                  'focus:outline-none focus:ring-2 focus:ring-sky-500',
                )}
              />
            </div>
            {/* Bonus Display */}
            {tokenAmount && parseFloat(tokenAmount) > 0 && (
              <div className="p-3 rounded-xl bg-emerald-50 border border-emerald-200">
                <div className="flex justify-between text-sm">
                  <span className="text-slate-600">Amount:</span>
                  <span className="font-medium">{parseFloat(tokenAmount).toFixed(2)} AZN</span>
                </div>
                <div className="flex justify-between text-sm text-emerald-600">
                  <span>Bonus ({conversionBonusPercent}%):</span>
                  <span className="font-medium">+{tokenBonusAmount.toFixed(2)} AZN</span>
                </div>
                <div className="flex justify-between text-sm font-bold mt-1 pt-1 border-t border-emerald-200">
                  <span>Total Credited:</span>
                  <span>{tokenTotalWithBonus.toFixed(2)} tokens</span>
                </div>
              </div>
            )}
          </div>
        }
      />

      {/* Send Withdrawal Popup */}
      <PopupMessage
        isOpen={popup === 'sendWithdrawal'}
        onClose={() => {
          setPopup(null);
          setWithdrawalAmount('');
          setWithdrawalCard('');
        }}
        title="Request Withdrawal"
        message="Withdraw your commission balance to your card. Your request will be processed by a worker."
        declineLabel="Cancel"
        proceedLabel="Request Withdrawal"
        proceedVariant="primary"
        proceedDisabled={!isWithdrawalAmountValid || !withdrawalCard || withdrawalCard.length < 13}
        isLoading={actionLoading || isCreatingWithdrawal}
        onDecline={() => {
          setPopup(null);
          setWithdrawalAmount('');
          setWithdrawalCard('');
        }}
        onProceed={() => {
          handleSendWithdrawal();
        }}
        customContent={
          <div className="space-y-3 mt-4">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Card Number</label>
              <input
                type="text"
                value={withdrawalCard}
                onChange={(e) => setWithdrawalCard(e.target.value.replace(/\D/g, '').slice(0, 16))}
                placeholder="Enter card number"
                maxLength={16}
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-50 border border-slate-200',
                  'text-sm text-slate-800 font-mono',
                  'focus:outline-none focus:ring-2 focus:ring-sky-500',
                )}
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Amount (AZN)</label>
              <input
                type="number"
                value={withdrawalAmount}
                onChange={(e) => setWithdrawalAmount(e.target.value)}
                placeholder={`Minimum ${MIN_REFEREE_WITHDRAWAL} AZN`}
                min={MIN_REFEREE_WITHDRAWAL}
                max={profile?.balance || 0}
                className={cn(
                  'w-full px-4 py-3 rounded-xl',
                  'bg-slate-50 border border-slate-200',
                  'text-sm text-slate-800',
                  'focus:outline-none focus:ring-2 focus:ring-sky-500',
                )}
              />
              {/* Minimum amount warning */}
              {withdrawalAmount && withdrawalAmountNum < MIN_REFEREE_WITHDRAWAL && (
                <p className="text-xs text-rose-500 mt-1">
                  Minimum withdrawal amount is {MIN_REFEREE_WITHDRAWAL} AZN
                </p>
              )}
            </div>
            {/* Balance info */}
            <div className="p-3 rounded-xl bg-slate-50 border border-slate-200">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Available Balance:</span>
                <span className="font-medium">{profile ? formatCurrency(profile.balance) : '-'}</span>
              </div>
              <div className="flex justify-between text-sm text-slate-500 mt-1">
                <span>Minimum Withdrawal:</span>
                <span>{MIN_REFEREE_WITHDRAWAL} AZN</span>
              </div>
            </div>
          </div>
        }
      />

      {/* Withdraw Nudge Popup - encourages Send Tokens instead */}
      <PopupMessage
        isOpen={popup === 'withdrawNudge'}
        onClose={() => setPopup(null)}
        title="Did you know?"
        message={`You can get a ${conversionBonusPercent}% bonus if you use Send Tokens instead of withdrawal! The player will receive extra tokens at no cost to you.`}
        declineLabel="Send Tokens"
        proceedLabel="Continue to Withdraw"
        proceedVariant="primary"
        onDecline={() => {
          setPopup('sendTokens');
        }}
        onProceed={() => {
          setPopup('sendWithdrawal');
        }}
      />
    </StaffLayout>
  );
}
